import type { Language } from '@eformulieren/engine'
import type { PersonForExternal } from '@eformulieren/model'
import { nwoEmployeeLabel } from '../collab/employee.js'
import { type ReactNode, type RefObject, useEffect, useId, useRef } from 'react'
import { formatTime } from '../comments/ThreadCard.js'
import type { ColorPair } from '../collab/ychange.js'
import { inLanguage } from '../language/text.js'
import type { EarlierVersie } from './Form.js'

/**
 * Het paneel Geschiedenis en de banner op het formulier (R-95, R-97, R-174, R-188, ontwerp 6.9,
 * CitatenPOC `HistoryPanel.tsx`).
 *
 * **Twee soorten standen, en ze heten verschillend.** Binnen deze beurt de standen die de pod om de
 * paar minuten bewaart, en het begin van de beurt: die zijn tot op de zin te tonen, met de kleur van
 * wie schreef (6.9), want ze horen bij het document dat nu open is. Daaronder de eerder ingediende
 * versies (R-174): die komen uit een andere beurt en staan per vraag naast de huidige (R-96), zoals de
 * keuzelijst boven het formulier dat al deed. Een snapshot van een andere beurt is tegen dit document
 * niet te tonen (ontwerp 6.9), dus die twee lopen niet door elkaar.
 *
 * **Bekijken en vergelijken zijn de twee weergaven van de POC.** Bekijken toont de stand van toen, met
 * per tekstfragment de kleur van wie het schreef; vergelijken toont de stand van nu, met wat er sinds
 * toen bij kwam gekleurd en wat verdween doorgehaald. In beide is niets te bewerken.
 *
 * **Eruit gaat met de knop Terug naar nu op het formulier**, en niet door het paneel te sluiten: zoals
 * in de POC blijft de weergave staan als het paneel dicht gaat, en de banner zegt dan nog steeds wat u
 * ziet en hoe u terugkomt. Op een telefoon en een smal scherm gaat het paneel na het kiezen dicht en
 * staat de focus op de banner, want anders ziet u een formulier dat niet reageert zonder te weten
 * waarom.
 */

/** Een bewaarde stand binnen deze beurt, zoals de pod hem levert (`GET /versiemomenten`). */
export interface VersionMoment {
  readonly id: string
  /** Het begin van de beurt (de nulsnapshot van de generatie), of een stand die de pod bewaarde. */
  readonly soort: 'begin' | 'automatisch'
  readonly moment: string
  /** Wie sinds de vorige stand schreef; een medewerker van NWO als merkteken, zonder persoon (R-66a, B6). */
  readonly auteurs: readonly PersonForExternal[]
  /** Per sectie de stand op dat moment; een sectie die toen niet veranderde staat er niet in. */
  readonly secties: readonly { readonly sectieId: string; readonly snapshot: Uint8Array }[]
}

export type HistoryWay = 'stand' | 'verschil'

/**
 * Eén begin van de beurt, en niet één per sectie.
 *
 * Elke sectie opent haar generatie op haar eigen moment, een paar milliseconden of een dag na de andere
 * (een sectie gaat pas open als iemand haar opent), en de pod levert per moment een regel. Voor de lezer
 * is er één begin: het vroegste moment, met de nulsnapshot van elke sectie erin. Een latere stand blijft
 * zoals hij is.
 */
export function oneBeginning(moments: readonly VersionMoment[]): readonly VersionMoment[] {
  const beginnings = moments.filter((entry) => entry.soort === 'begin')
  if (beginnings.length <= 1) return moments
  const first = [...beginnings].sort((a, b) => a.moment.localeCompare(b.moment))[0] as VersionMoment
  const merged: VersionMoment = { ...first, auteurs: [], secties: beginnings.flatMap((entry) => entry.secties) }
  return [merged, ...moments.filter((entry) => entry.soort !== 'begin')]
}

export interface HistoryChoice {
  readonly id: string
  readonly wijze: HistoryWay
}

const T = {
  uitleg: {
    nl: 'Het formulier bewaart om de paar minuten een stand. Kies een stand om hem te bekijken, of om te zien wat er sindsdien veranderde.',
    en: 'The form saves a version every few minutes. Choose one to view it, or to see what has changed since.',
  },
  binnen: { nl: 'In deze beurt', en: 'In this turn' },
  geen: { nl: 'Er is in deze beurt nog geen stand bewaard.', en: 'No version has been saved in this turn yet.' },
  begin: { nl: 'Begin van deze beurt', en: 'Start of this turn' },
  automatisch: { nl: 'Automatisch bewaard', en: 'Saved automatically' },
  door: { nl: 'Door', en: 'By' },
  niemand: { nl: 'Zonder wijziging van een persoon', en: 'Without a change by a person' },
  bekijken: { nl: 'Bekijken', en: 'View' },
  vergelijken: { nl: 'Vergelijken met nu', en: 'Compare with now' },
  ingediend: { nl: 'Eerder ingediend', en: 'Submitted earlier' },
  ingediendUitleg: {
    nl: 'Een eerdere versie legt u per vraag naast de huidige.',
    en: 'You place an earlier version next to the current one, question by question.',
  },
  naast: { nl: 'Naast de huidige leggen', en: 'Place next to the current one' },
  nietNaast: { nl: 'Niet meer naast de huidige', en: 'Stop placing next to the current one' },
  terug: { nl: 'Terug naar nu', en: 'Back to now' },
  bekijkt: { nl: 'U bekijkt de stand van {m}.', en: 'You are viewing the version of {m}.' },
  bekijktUitleg: {
    nl: 'Elk stuk tekst heeft de kleur van wie het schreef. U kunt hier niets wijzigen.',
    en: 'Each piece of text has the colour of whoever wrote it. You cannot change anything here.',
  },
  vergelijkt: { nl: 'U vergelijkt de stand van {m} met nu.', en: 'You are comparing the version of {m} with now.' },
  vergelijktUitleg: {
    nl: 'Gekleurd is sindsdien toegevoegd, doorgehaald is sindsdien verwijderd. U kunt hier niets wijzigen.',
    en: 'Coloured text was added since, struck-through text was removed since. You cannot change anything here.',
  },
  legenda: { nl: 'Wie schreef', en: 'Who wrote' },
  kapot: {
    nl: 'Deze stand is niet te lezen. U ziet weer de stand van nu.',
    en: 'This version cannot be read. You are seeing the current version again.',
  },
} as const

/** Het moment als "31 okt, 10:42", zoals bij een opmerking (`formatTime`). */
function momentName(moment: string, language: Language): string {
  return formatTime(moment, language)
}

export function HistoryOverview({
  taal: language,
  momenten: moments,
  gekozen: chosen,
  naamVan: nameOf,
  onKies: onChoose,
  eerdereVersies: earlier = [],
  gekozenVersie: chosenVersie,
  versieNaam: versieName,
  onEerdereVersie: onEarlier,
  onOpen,
}: {
  readonly taal: Language
  readonly momenten: readonly VersionMoment[]
  readonly gekozen: HistoryChoice | undefined
  readonly naamVan: (person: string) => string
  readonly onKies: (choice: HistoryChoice) => void
  readonly eerdereVersies?: readonly EarlierVersie[]
  readonly gekozenVersie?: string
  readonly versieNaam?: (versie: EarlierVersie) => string
  readonly onEerdereVersie?: (id: string | undefined) => void
  /** Bij het openen van het paneel: de standen opnieuw ophalen, want de pod bewaart er om de paar minuten een. */
  readonly onOpen?: () => void
}): ReactNode {
  const withinId = useId()
  const opened = useRef(onOpen)
  useEffect(() => opened.current?.(), [])
  const earlierId = useId()
  const newestFirst = [...moments].sort((a, b) => b.moment.localeCompare(a.moment))
  return (
    <div className="ef-geschiedenis">
      <p className="ef-geschiedenis__uitleg">{inLanguage(T.uitleg, language)}</p>
      <section className="ef-geschiedenis__groep" aria-labelledby={withinId}>
        <h3 className="ef-geschiedenis__kop" id={withinId}>
          {inLanguage(T.binnen, language)}
        </h3>
        {newestFirst.length === 0 ? (
          <p className="ef-geschiedenis__leeg">{inLanguage(T.geen, language)}</p>
        ) : (
          <ol className="ef-geschiedenis__lijst">
            {newestFirst.map((entry) => {
              const name = `${momentName(entry.moment, language)}, ${inLanguage(entry.soort === 'begin' ? T.begin : T.automatisch, language)}`
              const active = chosen?.id === entry.id
              return (
                <li key={entry.id} className="ef-geschiedenis__stand" data-gekozen={active ? 'ja' : 'nee'} aria-current={active ? 'true' : undefined}>
                  <p className="ef-geschiedenis__moment">
                    <time dateTime={entry.moment}>{momentName(entry.moment, language)}</time>
                    <span className="ef-geschiedenis__soort">{inLanguage(entry.soort === 'begin' ? T.begin : T.automatisch, language)}</span>
                  </p>
                  {entry.soort === 'begin' ? null : (
                    <p className="ef-geschiedenis__auteurs">
                      {entry.auteurs.length === 0
                        ? inLanguage(T.niemand, language)
                        : `${inLanguage(T.door, language)}: ${[...new Set(entry.auteurs.map((author) => (typeof author === 'string' ? nameOf(author) : nwoEmployeeLabel(language))))].join(', ')}`}
                    </p>
                  )}
                  <div className="ef-geschiedenis__acties">
                    <button
                      type="button"
                      className="ef-actieknop"
                      data-soort="secundair"
                      aria-pressed={active && chosen?.wijze === 'stand'}
                      aria-label={`${inLanguage(T.bekijken, language)}: ${name}`}
                      onClick={() => onChoose({ id: entry.id, wijze: 'stand' })}
                    >
                      {inLanguage(T.bekijken, language)}
                    </button>
                    {/*
                      Niet bij het begin van de beurt: dat begin is de vorige revisie, en het verschil daarmee is
                      wat R-88 de invuller buiten de toets niet toont. Bekijken kan wel (R-174). Ligt bij de
                      gebruiker; tot die beslist staat vergelijken alleen bij standen binnen de beurt.
                    */}
                    {entry.soort === 'begin' ? null : (
                    <button
                      type="button"
                      className="ef-actieknop"
                      data-soort="secundair"
                      aria-pressed={active && chosen?.wijze === 'verschil'}
                      aria-label={`${inLanguage(T.vergelijken, language)}: ${name}`}
                      onClick={() => onChoose({ id: entry.id, wijze: 'verschil' })}
                    >
                      {inLanguage(T.vergelijken, language)}
                    </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </section>
      {earlier.length === 0 || onEarlier === undefined || versieName === undefined ? null : (
        <section className="ef-geschiedenis__groep" aria-labelledby={earlierId}>
          <h3 className="ef-geschiedenis__kop" id={earlierId}>
            {inLanguage(T.ingediend, language)}
          </h3>
          <p className="ef-geschiedenis__uitleg">{inLanguage(T.ingediendUitleg, language)}</p>
          <ol className="ef-geschiedenis__lijst">
            {[...earlier].reverse().map((versie) => {
              const placed = chosenVersie === versie.id
              return (
                <li key={versie.id} className="ef-geschiedenis__stand" data-gekozen={placed ? 'ja' : 'nee'}>
                  <p className="ef-geschiedenis__moment">{versieName(versie)}</p>
                  <div className="ef-geschiedenis__acties">
                    <button
                      type="button"
                      className="ef-actieknop"
                      data-soort="secundair"
                      aria-pressed={placed}
                      aria-label={`${inLanguage(placed ? T.nietNaast : T.naast, language)}: ${versieName(versie)}`}
                      onClick={() => onEarlier(placed ? undefined : versie.id)}
                    >
                      {inLanguage(placed ? T.nietNaast : T.naast, language)}
                    </button>
                  </div>
                </li>
              )
            })}
          </ol>
        </section>
      )}
    </div>
  )
}

/** De legenda bij de kleuren: de naam staat er altijd bij, want kleur is nooit de enige drager (R-139). */
export interface LegendEntry {
  readonly naam: string
  readonly kleur: ColorPair
}

/**
 * De banner boven het formulier zolang er een stand uit de geschiedenis staat: wat u ziet, wat de
 * kleuren betekenen, en de weg terug. Escape in de banner gaat ook terug.
 */
export function HistoryBanner({
  taal: language,
  moment,
  wijze: way,
  legenda: legendEntries,
  kapot: broken,
  bannerRef,
  onTerug: onBack,
}: {
  readonly taal: Language
  readonly moment: string
  readonly wijze: HistoryWay
  readonly legenda: readonly LegendEntry[]
  readonly kapot: boolean
  readonly bannerRef?: RefObject<HTMLDivElement | null>
  readonly onTerug: () => void
}): ReactNode {
  const headerId = useId()
  const sentence = inLanguage(way === 'stand' ? T.bekijkt : T.vergelijkt, language).replace('{m}', momentName(moment, language))
  return (
    <div
      ref={bannerRef}
      className="ef-geschiedenisband"
      role="region"
      aria-labelledby={headerId}
      tabIndex={-1}
      data-wijze={way}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        onBack()
      }}
    >
      <div className="ef-geschiedenisband__tekst">
        <p className="ef-geschiedenisband__kop" id={headerId}>
          {sentence}
        </p>
        <p className="ef-geschiedenisband__uitleg">{inLanguage(way === 'stand' ? T.bekijktUitleg : T.vergelijktUitleg, language)}</p>
        {broken ? (
          <p className="ef-geschiedenisband__fout" role="alert">
            {inLanguage(T.kapot, language)}
          </p>
        ) : null}
        {legendEntries.length === 0 ? null : (
          <ul className="ef-geschiedenisband__legenda" aria-label={inLanguage(T.legenda, language)}>
            {legendEntries.map((entry) => (
              <li key={entry.naam} className="ef-geschiedenisband__auteur">
                <span className="ef-geschiedenisband__stip" aria-hidden="true" style={{ '--ef-auteurkleur': entry.kleur.dark } as Record<string, string>} />
                {entry.naam}
              </li>
            ))}
          </ul>
        )}
      </div>
      <button type="button" className="ef-actieknop" data-soort="primair" onClick={onBack}>
        {inLanguage(T.terug, language)}
      </button>
    </div>
  )
}
